import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import React, { useState, useEffect } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Button,
  ClickAwayListener,
  CircularProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Search as SearchIcon,
  ExpandLess,
  ExpandMore,
  Cancel as CancelIcon,
  Lock as LockIcon,
  Message as MessageIcon,
  Group as GroupIcon,
  DataUsage as DataUsageIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Build as BuildIcon,
  Business as BusinessIcon,
  Notifications as NotificationsIcon,
  ContactMail as ContactMailIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../../store/slices/auth.slice";
import {
  fetchUserRoles,
  fetchRolePermissions,
  fetchUserEffectivePermissions,
} from "../../store/slices/permissions.slice";
import "./Sidebar.css";
import { useTheme } from "@mui/material/styles";

const Sidebar = ({ isOpen = true }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  // Track the last fetched role to prevent duplicate API calls
  const [lastFetchedRoleId, setLastFetchedRoleId] = useState(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { menuPermissions, isLoading, error } = useSelector(
    (state) => state.permissions
  );

  useEffect(() => {
    // If user is authenticated
    if (user) {
      // Use new effective permissions endpoint that requires both userId and roleId
      if (user.user_id && user.primary_role_id) {
        // Only fetch if the role has changed and we haven't already fetched for this role
        if (lastFetchedRoleId !== user.primary_role_id) {
          setLastFetchedRoleId(user.primary_role_id);
          dispatch(fetchUserEffectivePermissions({ 
            userId: user.user_id, 
            roleId: user.primary_role_id 
          }));
        }
      }
      // Fallback: If user_id is available but not primary_role_id, fetch roles first
      else if (user.user_id || user.id) {
        const userId = user.user_id || user.id;
        dispatch(fetchUserRoles(userId))
          .unwrap()
          .then((roles) => {
            if (roles && roles.length > 0) {
              // Get permissions for the primary role
              const primaryRole =
                roles.find((role) => role.is_primary) || roles[0];
              if (primaryRole?.role_id && lastFetchedRoleId !== primaryRole.role_id) {
                setLastFetchedRoleId(primaryRole.role_id);
                dispatch(fetchUserEffectivePermissions({ 
                  userId: userId, 
                  roleId: primaryRole.role_id 
                }));
              }
            } else {
              setLoading(false);
            }
          })
          .catch(() => {
            setLoading(false);
          });
      }
    } else {
      setMenuItems(getDefaultMenuItems());
      setLoading(false);
    }
  }, [dispatch, user, lastFetchedRoleId]);

  // Update menu items when permissions change
  useEffect(() => {
    if (menuPermissions && menuPermissions.length > 0) {
      // Check for different API response formats
      if (menuPermissions[0]?.am_menu_configuration) {
        // This is the new API format with am_menu_configuration and children
        const treeBasedMenu = convertTreeToMenuItems(menuPermissions);
        if (treeBasedMenu && treeBasedMenu.length > 0) {
          setMenuItems(treeBasedMenu);
        } else {
          // Fallback to flat structure
          setMenuItems(mapPermissionsToMenuItems(menuPermissions));
        }
      } else if (menuPermissions[0]?.children) {
        // This is the processed tree structure
        const treeBasedMenu = convertTreeToMenuItems(menuPermissions);
        if (treeBasedMenu && treeBasedMenu.length > 0) {
          setMenuItems(treeBasedMenu);
        } else {
          // Fallback to flat structure
          setMenuItems(mapPermissionsToMenuItems(menuPermissions));
        }
      } else {
        // Old flat structure - map permissions to menu items
        const permissionBasedMenu = mapPermissionsToMenuItems(menuPermissions);
        setMenuItems(permissionBasedMenu);
      }
    } else if (!isLoading) {
      // If no permissions and not loading, show empty menu
      setMenuItems([]);
    }
    setLoading(isLoading);
  }, [menuPermissions, isLoading, user]);

  // Map permissions to menu items with icons
  const mapPermissionsToMenuItems = (permissions) => {
    const menuMap = {};
    const rootMenus = [];
    const parentMenuIds = new Set();

    if (!permissions || permissions.length === 0) {
      return [];
    }

    // First pass: create all menu items and track parent menu IDs
    permissions.forEach((permission) => {
      // Handle both old format (with am_menu_configuration) and new format (direct properties)
      const menu = permission.am_menu_configuration || permission;

      if (!menu || !menu.menu_code) {
        return;
      }

      const menuItem = {
        id: menu.menu_code,
        title: menu.menu_display_name || menu.menu_name,
        menuId: menu.menu_id,
        parentMenuId: menu.parent_menu_id,
        type: menu.parent_menu_id ? "child" : "single",
        path: menu.route_url || `/menu/${menu.menu_code.toLowerCase()}`,
        icon: getIconForMenu(menu.menu_code, menu.icon_class),
        children: [],
      };

      menuMap[menu.menu_id] = menuItem;

      // Track parent menu IDs for second pass
      if (menu.parent_menu_id) {
        parentMenuIds.add(menu.parent_menu_id);
      }
    });

    // Create missing parent menus
    parentMenuIds.forEach((parentId) => {
      if (!menuMap[parentId]) {
        // Create a placeholder parent menu
        const parentCode = `MENU_${parentId}`;
        menuMap[parentId] = {
          id: parentCode,
          title: parentId === 2 ? "User Management" : `Menu ${parentId}`,
          menuId: parentId,
          parentMenuId: null,
          type: "group",
          children: [],
          icon: getIconForMenu(parentId === 2 ? "USER_MANAGEMENT" : parentCode),
        };
      }
    });

    // Second pass: organize into hierarchy
    Object.values(menuMap).forEach((menuItem) => {
      if (!menuItem.parentMenuId) {
        // This is a root menu
        rootMenus.push(menuItem);
      } else if (menuMap[menuItem.parentMenuId]) {
        // This is a child menu
        const parentMenu = menuMap[menuItem.parentMenuId];
        parentMenu.type = "group";
        parentMenu.children = parentMenu.children || [];
        parentMenu.children.push(menuItem);
      } else {
        // Parent menu doesn't exist, add as root
        rootMenus.push(menuItem);
      }
    });

    return rootMenus.length > 0 ? rootMenus : getDefaultMenuItems();
  };

  // Convert tree structure to menu items
  const convertTreeToMenuItems = (tree) => {
    if (!tree || tree.length === 0) {
      return [];
    }

    return tree
      .map((node) => {
        // For the new API response format with am_menu_configuration
        if (node.am_menu_configuration) {
          const menu = node.am_menu_configuration;
          const menuItem = {
            id: menu.menu_code,
            title: menu.menu_display_name || menu.menu_name,
            type:
              node.children && node.children.length > 0 ? "collapse" : "item",
            path: menu.route_url || `#${menu.menu_code}`,
            icon: getIconForMenu(menu.menu_code, menu.icon_class),
            isRestricted: node.is_restricted || false,
            isActive: node.is_active !== false,
          };

          if (node.children && node.children.length > 0) {
            menuItem.children = convertTreeToMenuItems(node.children);
          }

          return menuItem;
        }

        // For direct API response format (as seen in the console output)
        if (node.menu_code) {
          const menuItem = {
            id: node.menu_code,
            title: node.menu_display_name || node.menu_name,
            type:
              node.children && node.children.length > 0 ? "collapse" : "item",
            path: node.route_url || `#${node.menu_code}`,
            icon: getIconForMenu(node.menu_code, node.icon_class),
          };

          if (node.children && node.children.length > 0) {
            menuItem.children = convertTreeToMenuItems(node.children);
          }

          return menuItem;
        }

        // For previously processed format
        if (!node.id) {
          return null;
        }

        const menuItem = {
          id: node.id,
          title: node.title || node.id, // Fallback to id if title is missing
          type: node.children && node.children.length > 0 ? "collapse" : "item",
          path: node.path || `#${node.id}`, // Fallback path
          icon: getIconForMenu(node.id, node.classIcon),
          isRestricted: node.isRestricted || false,
          isActive: node.isActive !== false,
        };

        if (node.children && node.children.length > 0) {
          menuItem.children = convertTreeToMenuItems(node.children);
        }

        return menuItem;
      })
      .filter(Boolean); // Filter out null items
  };

  // Get appropriate icon for menu based on menu code or class icon
  const getIconForMenu = (menuCode, classIcon) => {
    if (classIcon) {
      // If we have a class icon, we could render it here
      // For now, we'll still use our icon mapping
    }

    const iconMap = {
      RECORDINGS: <VideoLibraryIcon fontSize="small" />,
      DASHBOARD: <DashboardIcon fontSize="small" />,
      USER_MANAGEMENT: <GroupIcon fontSize="small" />,
      MASTER_MANAGEMENT: <DataUsageIcon fontSize="small" />,
      SCREEN_BUILDER: <BuildIcon fontSize="small" />,
      NOTIFICATION_MANAGEMENT: <NotificationsIcon fontSize="small" />,
      LEAD_MANAGEMENT: <ContactMailIcon fontSize="small" />,
      LOB_MANAGEMENT: <BusinessIcon fontSize="small" />,
      PRODUCT_MANAGEMENT: <BusinessIcon fontSize="small" />,
      PDF_TEMPLATE_MAPPING: <PictureAsPdfIcon fontSize="small" />,
      SEARCH: <SearchIcon fontSize="small" />,
      // CANCELLATION: <CancelIcon fontSize="small" />,
      COMMUNICATION: <MessageIcon fontSize="small" />,
      PASSWORD: <LockIcon fontSize="small" />,
      ROLES: <LockIcon fontSize="small" />,
      PERMISSIONS: <LockIcon fontSize="small" />,
      USERS: <GroupIcon fontSize="small" />,
    };

    return iconMap[menuCode] || <MenuIcon fontSize="small" />;
  };

  // Get default menu items when no permissions are available
  const getDefaultMenuItems = () => {
    return [
      {
        id: "DASHBOARD",
        title: "Dashboard",
        path: "/dashboard",
        icon: <DashboardIcon />,
      },
    ];
  };

  const handleItemClick = (item) => {
    // Check if the item has children - if so, toggle expansion
    if (item.children && item.children.length > 0) {
      setExpanded((prev) => (prev === item.id ? null : item.id));
      return;
    }

    // For all menu items with paths (no children), navigate directly
    if (item.path) {
      // For hash routes or placeholder paths, show the under construction page
      if (item.path.startsWith("#")) {
        // Use the item ID to create a consistent route for the page under construction
        navigate(`/page-under-construction/${item.id.toLowerCase()}`);
      } else {
        // For regular paths, navigate directly
        navigate(item.path);
      }
      setExpanded(null);
    }
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
    } catch (error) {
      } finally {
      navigate("/login");
    }
  };

  const isActive = (path) => location.pathname === path;

  const handleClickAway = () => {
    setExpanded(null);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: "relative" }}>
        <Drawer
          variant="permanent"
          open={isOpen}
          className={`sidebar ${isOpen ? "open" : "closed"}`}
          PaperProps={{
            sx: {
              overflow: "visible",
              width: isOpen ? 220 : 0,
              minWidth: isOpen ? 220 : 0,
              backgroundColor: "#2E3A59", // Blue background
              color: "#ffffff",
              height: "calc(100vh - 57px)",
              top: "57px",
              borderRight: "none",
              visibility: isOpen ? "visible" : "hidden",
              transition: "width 0.3s ease, visibility 0.3s ease",
            },
          }}
        >
          <Box
            sx={{
              width: isOpen ? 220 : 0,
              minWidth: isOpen ? 220 : 0,
              display: "flex",
              flexDirection: "column",
              visibility: isOpen ? "visible" : "hidden",
              backgroundColor: "#2E3A59",
              height: "100%",
            }}
          >
            <Box sx={{ flex: 1, overflow: "auto", backgroundColor: "#2E3A59" }}>
              {loading ? (
                <Box
                  sx={{ padding: 2, display: "flex", justifyContent: "center" }}
                >
                  <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                </Box>
              ) : error ? (
                <Box sx={{ padding: 2 }}>
                  <Alert severity="error" sx={{ fontSize: "0.8rem" }}>
                    Failed to load menu
                  </Alert>
                </Box>
              ) : menuItems.length === 0 ? (
                <Box sx={{ padding: 2 }}>
                  <Alert severity="info" sx={{ fontSize: "0.8rem" }}>
                    No permissions assigned
                  </Alert>
                </Box>
              ) : (
                menuItems.map((item) => (
                  <Box key={item.id} sx={{ position: "relative" }}>
                    <ListItemButton
                      onClick={() => handleItemClick(item)}
                      selected={isActive(item.path)}
                      sx={{
                        pl: 2,
                        backgroundColor: isActive(item.path)
                          ? "#E31E25"
                          : "transparent",
                        "&:hover": {
                          backgroundColor: isActive(item.path) ? "#E31E25" : "rgba(255,255,255,0.1)",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "#E31E25",
                        },
                        py: 1.5,
                        my: 0.2,
                      }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: "auto", mr: 1.5, color: "#ffffff" }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {isOpen && (
                        <Tooltip title={item.title} placement="right" arrow>
                          <ListItemText
                            primary={item.title}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              "& .MuiTypography-root": {
                                fontSize: "0.8rem",
                                fontWeight: 400,
                                color: "#ffffff",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              },
                            }}
                          />
                        </Tooltip>
                      )}
                      {(item.type === "group" ||
                        item.type === "collapse" ||
                        (item.children && item.children.length > 0)) &&
                        (expanded === item.id ? (
                          <ExpandLess sx={{ color: "#ffffff" }} />
                        ) : (
                          <ExpandMore sx={{ color: "#ffffff" }} />
                        ))}
                    </ListItemButton>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Drawer>

        {/* Submenu panel - follows same blue/red pattern as main sidebar */}
      {expanded &&
        menuItems.map((item, itemIndex) => {
          if (
            item.id === expanded &&
            item.children &&
            item.children.length > 0
          ) {
            const topOffset = 57 + itemIndex * 56; // Calculate position based on menu item index
            return (
              <Box
                key={`submenu-${item.id}`}
                sx={{
                  position: "fixed",
                  left: isOpen ? "220px" : "72px",
                  top: `${topOffset}px`,
                  width: 200,
                  maxHeight: "300px",
                  backgroundColor: "#2E3A59",
                  boxShadow: "2px 2px 10px rgba(0,0,0,0.3)",
                  zIndex: 1400,
                  overflowY: "auto",
                  borderRadius: "0 4px 4px 0",
                  borderLeft: "3px solid #E31E25",
                }}
              >
                {item.children.map((child, index) => (
                  <Box
                    key={child.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleItemClick(child);
                    }}
                    sx={{
                      padding: "10px 16px",
                      backgroundColor: isActive(child.path)
                        ? "#E31E25"
                        : "transparent",
                      cursor: "pointer",
                      borderBottom:
                        index < item.children.length - 1
                          ? "1px solid rgba(255,255,255,0.1)"
                          : "none",
                      "&:hover": {
                        backgroundColor: isActive(child.path) ? "#E31E25" : "rgba(255,255,255,0.1)",
                      },
                      transition: "background-color 0.2s ease",
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: isActive(child.path) ? 600 : 400,
                        color: "#ffffff",
                      }}
                    >
                      {child.title}
                    </Box>
                  </Box>
                ))}
              </Box>
            );
          }
          return null;
        })}
      </Box>
    </ClickAwayListener>
  );
};

export default Sidebar;
